import { useEffect, useRef, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import { darken, makeStyles } from '@material-ui/core/styles';
import {
  UserEmailSearchField,
  UserSearchOption,
} from '../UserEmailSearchField';

type MembershipChanges = {
  addUsers: string[];
  removeUsers: string[];
};

interface UserGroupMember {
  id: string;
  displayName: string;
  email: string;
}

const useStyles = makeStyles(theme => ({
  panel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(4),
    padding: theme.spacing(4),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(2) },
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: theme.spacing(1),
    },
  },
  member: {
    flex: 1,
    minWidth: 0,
    overflowWrap: 'anywhere',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    padding: theme.spacing(1.5, 2),
  },
  add: {
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    border: `1px solid ${theme.palette.secondary.main}`,
    '&:hover': {
      backgroundColor: darken(theme.palette.secondary.main, 0.15),
    },
    minWidth: 180,
  },
  remove: {
    borderRadius: theme.spacing(2),
    backgroundColor: '#f8cecc',
    color: '#7b2020',
    border: '1px solid #b85450',
    '&:hover': { backgroundColor: '#f2b6b3' },
    minWidth: 180,
  },
}));

export const UserGroupMembersField = ({
  onChange,
  formData,
  formContext,
}: FieldExtensionComponentProps<MembershipChanges>) => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [members, setMembers] = useState<UserGroupMember[]>([]);
  const [pendingUsers, setPendingUsers] = useState<UserSearchOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserSearchOption>();
  const [signedInUserEmail, setSignedInUserEmail] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addError, setAddError] = useState<string>();
  const selectedGroupName = formContext?.formData?.user_group as
    | string
    | undefined;
  const changes = formData ?? { addUsers: [], removeUsers: [] };
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const previousGroupRef = useRef(selectedGroupName);

  useEffect(() => {
    let cancelled = false;
    setMembers([]);
    setError(undefined);
    setDialogOpen(false);
    setSelectedUser(undefined);
    setAddError(undefined);
    if (previousGroupRef.current !== selectedGroupName) {
      previousGroupRef.current = selectedGroupName;
      setPendingUsers([]);
      onChangeRef.current({ addUsers: [], removeUsers: [] });
    }
    if (!selectedGroupName) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const load = async () => {
      try {
        const profile = await identityApi.getProfileInfo();
        const email = profile.email?.trim();
        if (!email) {
          throw new Error('Signed-in user has no email address');
        }
        const azureBase = await discoveryApi.getBaseUrl('azure');
        const response = await fetchApi.fetch(
          `${azureBase}/users/groups?email=${encodeURIComponent(email)}`,
        );
        if (!response.ok) {
          throw new Error(`groups:${response.status}`);
        }
        const groups = (await response.json()) as {
          id: string;
          displayName: string;
        }[];
        const group = groups.find(item => item.displayName === selectedGroupName);
        if (!group) {
          throw new Error('The selected group could not be found');
        }
        const membersResponse = await fetchApi.fetch(
          `${azureBase}/users/groups/${encodeURIComponent(
            group.id,
          )}/members?email=${encodeURIComponent(email)}`,
        );
        if (!membersResponse.ok) {
          throw new Error(`members:${membersResponse.status}`);
        }
        const loaded = (await membersResponse.json()) as UserGroupMember[];
        if (!cancelled) {
          setMembers(loaded);
          setSignedInUserEmail(email.toLowerCase());
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load the users in the selected group');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, identityApi, selectedGroupName]);

  const addMember = () => {
    if (!selectedUser) return;
    const email = selectedUser.email.trim().toLowerCase();
    if (
      changes.addUsers.includes(email) ||
      members.some(member => member.email.toLowerCase() === email)
    ) {
      setAddError('This user is already in the group or pending additions');
      return;
    }
    setPendingUsers(current => [...current, { ...selectedUser, email }]);
    onChange({ ...changes, addUsers: [...changes.addUsers, email] });
    setSelectedUser(undefined);
    setAddError(undefined);
    setDialogOpen(false);
  };

  const toggleRemoval = (member: UserGroupMember) => {
    const email = member.email.toLowerCase();
    if (email === signedInUserEmail) return;
    onChange({
      ...changes,
      removeUsers: changes.removeUsers.includes(email)
        ? changes.removeUsers.filter(value => value !== email)
        : [...changes.removeUsers, email],
    });
  };

  if (!selectedGroupName) {
    return <Typography>Select a user group to manage its members.</Typography>;
  }

  return (
    <Box className={classes.panel}>
      <Box className={classes.row}>
        <Typography variant="subtitle1" className={classes.member}>
          {selectedGroupName}
        </Typography>
        <Button
          className={classes.add}
          disabled={loading || Boolean(error)}
          onClick={() => setDialogOpen(true)}
        >
          Add Members
        </Button>
      </Box>
      <Typography variant="body2" paragraph>
        Changes are pending until you submit the request. You cannot remove your
        own account.
      </Typography>
      {loading && <CircularProgress size={20} />}
      {error && <Typography color="error">{error}</Typography>}
      {!loading && !error && members.length === 0 && (
        <Typography>No users found in this group</Typography>
      )}
      {!loading &&
        !error &&
        members.map(member => {
          const removing = changes.removeUsers.includes(
            member.email.toLowerCase(),
          );
          return (
            <Box className={classes.row} key={member.id}>
              <Box className={classes.member}>
                <Typography>{member.displayName}</Typography>
                <Typography variant="body2">{member.email}</Typography>
                {removing && (
                  <Typography variant="caption">Pending removal</Typography>
                )}
              </Box>
              <Button
                className={classes.remove}
                disabled={member.email.toLowerCase() === signedInUserEmail}
                aria-label={`${removing ? 'Undo removal of' : 'Remove'} ${member.displayName}`}
                onClick={() => toggleRemoval(member)}
              >
                {removing ? 'Undo Removal' : 'Remove Member'}
              </Button>
            </Box>
          );
        })}
      {changes.addUsers.map(email => (
        <Box className={classes.row} key={email}>
          <Box className={classes.member}>
            <Typography>
              {pendingUsers.find(user => user.email === email)?.displayName ??
                email}
            </Typography>
            <Typography variant="body2">{email}</Typography>
            <Typography variant="caption">Pending addition</Typography>
          </Box>
          <Button
            className={classes.remove}
            aria-label={`Cancel addition of ${email}`}
            onClick={() =>
              onChange({
                ...changes,
                addUsers: changes.addUsers.filter(value => value !== email),
              })
            }
          >
            Cancel Addition
          </Button>
        </Box>
      ))}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedUser(undefined);
          setAddError(undefined);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Members</DialogTitle>
        <DialogContent>
          <UserEmailSearchField
            label="Search user to add"
            value={selectedUser?.email}
            onChange={email => {
              if (!email) setSelectedUser(undefined);
            }}
            onUserChange={user => {
              setSelectedUser(user);
              setAddError(undefined);
            }}
            helperText="Select an Entra account. Data Engineer, Data Analyst and Data Scientist groups require admin accounts."
          />
          {addError && <Typography color="error">{addError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              setSelectedUser(undefined);
              setAddError(undefined);
            }}
          >
            Cancel
          </Button>
          <Button color="primary" disabled={!selectedUser} onClick={addMember}>
            Add to Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
